import type { Dayjs } from 'dayjs';
import type { ActivityDTO, InspectorDTO } from 'src/types/dto';
import type { AppointmentDTO } from 'src/types/dto/appointment.dto';
import { ActivityType } from 'src/types/dto';

import { formatRangeFromAppointments } from 'src/sections/visit/components/scheduled';
import { convertToTimezone, formatAppointmentShortDateInZone } from 'src/utils/timezone';

type AllocationDateLabelOptions = {
  /** Team-section pills use compact labels; pass false for tooltip/full range text. */
  compact?: boolean;
};

/** Compact pill label. Same day parts as shuffle `formatSelectedDateRangeLabel`, joined with an en dash and no spaces. */
export function formatCompactAllocationDateRange(from: Dayjs, to: Dayjs): string {
  if (from.isSame(to, 'day')) {
    return from.format('DD MMM YY');
  }

  if (from.isSame(to, 'month') && from.isSame(to, 'year')) {
    return `${from.format('DD')}–${to.format('DD MMM YY')}`;
  }

  if (from.isSame(to, 'year')) {
    return `${from.format('DD MMM')}–${to.format('DD MMM YY')}`;
  }

  return `${from.format('DD MMM YY')}–${to.format('DD MMM YY')}`;
}

export function formatCompactAllocationDateRangeInZone(
  startIso: string | Date,
  endIso: string | Date,
  timeZoneId?: string | null
): string {
  const tz = timeZoneId?.trim() || 'UTC';
  const from = convertToTimezone(startIso, tz);
  const to = convertToTimezone(endIso, tz);

  if (!from.isValid() || !to.isValid()) {
    return '';
  }

  return formatCompactAllocationDateRange(from, to);
}

function formatAllocationRangeFromAppointments(
  appointments: Array<Pick<AppointmentDTO, 'from' | 'to' | 'type' | 'timezoneId'>>,
  timezoneId?: string | null,
  options?: AllocationDateLabelOptions
): string | false {
  const sorted = appointments
    .filter((appointment) => appointment.type !== 'blocker')
    .sort((a, b) => convertToTimezone(a.from, 'UTC').diff(convertToTimezone(b.from, 'UTC')));

  if (sorted.length === 0) {
    return false;
  }

  const tz = timezoneId?.trim() || sorted[0]?.timezoneId?.trim() || 'UTC';
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const startInstant = first?.from;
  const endInstant = last?.to ?? last?.from;

  if (!startInstant) {
    return false;
  }

  if (!endInstant) {
    return formatAppointmentShortDateInZone(startInstant, tz) || false;
  }

  if (options?.compact) {
    const compactLabel = formatCompactAllocationDateRangeInZone(startInstant, endInstant, tz);
    return compactLabel || false;
  }

  return formatRangeFromAppointments(appointments, timezoneId) || false;
}

/**
 * Merge a saved allocation row with pending team selection.
 * Spreading pending over allocated copies appointment `earliestAvailability` unless pending
 * explicitly owns the preview window for a positive duration edit.
 */
export function mergeAllocatedInspectorWithPendingSelection<
  T extends InspectorDTO & { selectedDurationDays?: number | null },
>(allocatedInspector: T, pending?: T): T {
  if (!pending) {
    return allocatedInspector;
  }
  const merged = { ...allocatedInspector, ...pending };
  if (
    typeof pending.selectedDurationDays === 'number' &&
    Number.isFinite(pending.selectedDurationDays) &&
    pending.selectedDurationDays > 0
  ) {
    merged.earliestAvailability = pending.earliestAvailability;
  }
  return merged;
}

/** Pending Team rows carry per-resource audit days while the allocation is unsaved. */
function hasPendingTeamDurationSelection(
  row: InspectorDTO & { selectedDurationDays?: number | null }
): boolean {
  return (
    typeof row.selectedDurationDays === 'number' &&
    Number.isFinite(row.selectedDurationDays) &&
    row.selectedDurationDays > 0
  );
}

export function getRemovableInspectorDateLabel(
  row: InspectorDTO,
  activity: ActivityDTO,
  options?: AllocationDateLabelOptions
): string {
  const inspectorAppointments = activity?.appointments?.filter(
    (apt) => apt.inspector?.id === row.id
  ) as AppointmentDTO[] | undefined;
  const from = row.earliestAvailability?.from;
  const to = row.earliestAvailability?.to;
  const pendingDurationEdit = hasPendingTeamDurationSelection(row);
  const previewFromEarliestAvailability =
    pendingDurationEdit && from != null && to != null;
  const appointmentsFromSlots =
    previewFromEarliestAvailability || (!inspectorAppointments?.length && from != null && to != null)
      ? ([
          {
            from,
            to,
            timezoneId: row.earliestAvailability?.timezoneId,
          },
        ] as unknown as AppointmentDTO[])
      : [];
  const appointments = pendingDurationEdit
    ? previewFromEarliestAvailability
      ? appointmentsFromSlots
      : undefined
    : inspectorAppointments?.length
      ? inspectorAppointments
      : appointmentsFromSlots.length
        ? appointmentsFromSlots
        : undefined;

  const timezoneId =
    activity.type === ActivityType.DeskService ? row.timezoneId : activity?.site?.timezoneId;

  return (
    (appointments?.length
      ? formatAllocationRangeFromAppointments(appointments, timezoneId, options)
      : false) ||
    (!pendingDurationEdit && from
      ? formatAppointmentShortDateInZone(from, timezoneId || 'UTC')
      : null) ||
    '—'
  );
}

